import {
  appendSyntheticEntityTabs,
  buildEntityTabPath,
  type CompiledEntityTabDefinition,
  type CompiledEntityView,
  requireSyntheticEntityTab,
  resolveVisibleAuthoredEntityTabs,
  stripEntityTabPathSuffix,
} from "@repo/shared";

export const ENTITY_GENERIC_TAB_PREFIX = "entity.";

/** Re-exported from the shared synthetic-tab registry, which owns the key. */
export const ENTITY_ATTACHMENTS_TAB_KEY = requireSyntheticEntityTab("attachments").tabKey;

type EntityTabDefinitionsInput =
  | Pick<CompiledEntityView, "tabs" | "functionalityTabs">
  | null
  | undefined;

/**
 * The tabs an entity page actually renders: the compiled, enabled ones in list order, plus
 * the synthetic tabs the framework does not compile (Usage, Attachments) as the shared
 * registry orders and gates them.
 *
 * This is the *only* injection path. Pure, and shared with anything that needs to *link*
 * to a tab rather than render one — the Documents screen builds an Attachments-tab href
 * for a type it never mounts.
 */
export function buildEntityTabDefinitions(
  entityView: EntityTabDefinitionsInput
): CompiledEntityTabDefinition[] {
  if (entityView === undefined || entityView === null) {
    return [];
  }

  return appendSyntheticEntityTabs(resolveVisibleAuthoredEntityTabs(entityView), entityView);
}

/**
 * The route slug of an entity type's Attachments tab, or `null` when the type has none.
 *
 * The slug is not always `"attachments"`: `buildEntityTabSlugMaps` falls back to the full
 * tab key when two tabs would collide on the same short slug, and a link that assumed the
 * short form would 404 exactly on the types that collide.
 */
export function resolveEntityAttachmentsTabSlug(
  entityTypeKey: string,
  entityView: EntityTabDefinitionsInput
): string | null {
  const tabs = buildEntityTabDefinitions(entityView);
  if (tabs.length === 0) return null;
  return (
    buildEntityTabSlugMaps(entityTypeKey, tabs).slugByTabKey.get(ENTITY_ATTACHMENTS_TAB_KEY) ?? null
  );
}

function deriveRawTabSlug(entityTypeKey: string, tabKey: string): string {
  const typePrefix = `${entityTypeKey}.`;
  if (entityTypeKey && tabKey.startsWith(typePrefix)) {
    return tabKey.slice(typePrefix.length);
  }
  if (tabKey.startsWith(ENTITY_GENERIC_TAB_PREFIX)) {
    return tabKey.slice(ENTITY_GENERIC_TAB_PREFIX.length);
  }
  return tabKey;
}

export interface EntityTabSlugMaps {
  slugByTabKey: Map<string, string>;
  tabKeyBySlug: Map<string, string>;
}

export function buildEntityTabSlugMaps(
  entityTypeKey: string,
  tabs: CompiledEntityTabDefinition[]
): EntityTabSlugMaps {
  const rawByTabKey = new Map<string, string>();
  const counts = new Map<string, number>();

  for (const tab of tabs) {
    const raw = deriveRawTabSlug(entityTypeKey, tab.tabKey);
    rawByTabKey.set(tab.tabKey, raw);
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }

  const slugByTabKey = new Map<string, string>();
  const tabKeyBySlug = new Map<string, string>();
  for (const tab of tabs) {
    const raw = rawByTabKey.get(tab.tabKey) ?? tab.tabKey;
    const slug = (counts.get(raw) ?? 0) > 1 ? tab.tabKey : raw;
    slugByTabKey.set(tab.tabKey, slug);
    tabKeyBySlug.set(slug, tab.tabKey);
  }

  return { slugByTabKey, tabKeyBySlug };
}

export function resolveTabKeyFromSlug(maps: EntityTabSlugMaps, slug: string): string | null {
  return maps.tabKeyBySlug.get(slug) ?? null;
}

export function buildEntityCurrentPath(args: {
  baseEntityPath: string;
  activeTabKey: string | null;
  defaultTabKey: string | null;
  maps: EntityTabSlugMaps;
}): string {
  const { baseEntityPath, activeTabKey, defaultTabKey, maps } = args;
  if (!activeTabKey || activeTabKey === defaultTabKey) {
    return stripEntityTabPathSuffix(baseEntityPath);
  }

  const slug = maps.slugByTabKey.get(activeTabKey);
  return slug ? buildEntityTabPath(baseEntityPath, slug) : stripEntityTabPathSuffix(baseEntityPath);
}
